/**
 * Oversight analytics service for the ms-agents backend (Flex Comms module).
 *
 * Each function takes the ms-agents verb (imported from `commsApi.js`) as its
 * first argument. Return-shape note: `get` returns `response.data`; `post`
 * returns `{ data, status }`.
 *
 * Unlike `oversightService.js`, these functions rethrow the original axios
 * error (rather than wrapping it in `new Error(message)`) so callers can
 * inspect `err.response.status` and classify the failure via
 * `classifyOversightError`.
 */
import { buildQuery } from "./_query";

/**
 * List coaches with rollup analytics for a period.
 *
 * The list is roster-driven: assigned coaches with no activity in the range
 * come back with zeroes, so `limit`/`offset` page the roster rather than the
 * set of coaches who happened to be busy. Page with the payload's `total` /
 * `has_more` — a short page is NOT the end of the list.
 *
 * @param {Function} get - ms-agents GET verb.
 * @param {Object} [options] - `{ date_from, date_to, limit, offset,
 *   include_today, scope }`. `include_today` (default true server-side)
 *   computes the current UTC day live, which is what sets `partial` on the
 *   response. `scope` is `"reports"` (default) or `"company"` — the latter
 *   opts a manager out of report-chain narrowing and is a no-op for
 *   executives. Neither can widen past the caller's own company.
 * @returns {Promise<Object>} `{ coaches, total, has_more,
 *   hidden_unmapped_coaches, partial, percentiles_exact, read_latency_exact,
 *   date_from, date_to }`.
 * @throws {Error} The original axios error (with `response.status` intact).
 */
export const getAnalyticsCoaches = async (
  get,
  { date_from, date_to, limit, offset, include_today, scope } = {}
) => {
  try {
    return await get(
      `/api/v1/oversight/analytics/coaches${buildQuery({
        date_from,
        date_to,
        limit,
        offset,
        include_today,
        scope,
      })}`
    );
  } catch (err) {
    console.error(err?.response?.data?.message || "Error fetching coaches analytics");
    throw err;
  }
};

/**
 * Get analytics totals and a per-day/week series for a single coach.
 *
 * @param {Function} get - ms-agents GET verb.
 * @param {string} coachId - Coach id (user_id or employee_id).
 * @param {Object} [options] - `{ date_from, date_to, granularity,
 *   include_today, scope }` where `granularity` is `"day"` or `"week"`.
 * @returns {Promise<Object>} `{ coach_id, resolved_coach_user_id, coach_name,
 *   partial, totals, series }`. The series is zero-filled — every bucket in
 *   the range is present, including days with no activity.
 * @throws {Error} The original axios error (with `response.status` intact).
 */
export const getCoachAnalytics = async (
  get,
  coachId,
  { date_from, date_to, granularity, include_today, scope } = {}
) => {
  try {
    return await get(
      `/api/v1/oversight/analytics/coaches/${coachId}${buildQuery({
        date_from,
        date_to,
        granularity,
        include_today,
        scope,
      })}`
    );
  } catch (err) {
    console.error(err?.response?.data?.message || "Error fetching coach analytics");
    throw err;
  }
};

/**
 * Plan-authoring analytics for one coach, from the plan-events stream.
 *
 * Read `stream_active` before rendering any zero: it is false when the event
 * stream recorded nothing at all in the window (the `PLAN_EVENTS_ENABLED` flag
 * was off), which is not the same as "this coach wrote no plans".
 *
 * `clients_assigned` is point-in-time — subscription rows carry no assignment
 * window, so it is "currently assigned", never "assigned during the range".
 *
 * @param {Function} get - ms-agents GET verb.
 * @param {string} coachId - Coach id (user_id or employee_id).
 * @param {Object} [options] - `{ date_from, date_to, scope, actor_only }`.
 *   `actor_only` (default true server-side) counts work this coach did; false
 *   widens to any staff edit on their roster.
 * @returns {Promise<Object>} `{ coach_id, resolved_coach_user_id, date_from,
 *   date_to, actor_only, clients_assigned, stream_active, totals,
 *   change_distribution, series, needs_a_look, thresholds }`.
 * @throws {Error} The original axios error (with `response.status` intact).
 */
export const getCoachPlanAnalytics = async (
  get,
  coachId,
  { date_from, date_to, scope, actor_only } = {}
) => {
  try {
    return await get(
      `/api/v1/oversight/analytics/coaches/${coachId}/plans${buildQuery({
        date_from,
        date_to,
        scope,
        actor_only,
      })}`
    );
  } catch (err) {
    console.error(err?.response?.data?.message || "Error fetching coach plan analytics");
    throw err;
  }
};

/**
 * Support-desk ticket metrics for one coach.
 *
 * These are NOT the chat numbers and must not be shown as if they were.
 * `median_first_response_seconds` here is time to answer a TICKET (from the SLA
 * record); the identically-named field on `getCoachAnalytics` is time to reply
 * in a chat room. `csat_avg` here is attributed to the ticket's assigned agent,
 * while the coach rollup's CSAT is attributed by room access — different
 * questions over the same ratings, and they will not agree.
 *
 * Every metric is nullable: a coach who worked no tickets returns nulls so the
 * UI renders a dash, not a measured zero. `reopen_rate` is null rather than 0
 * when nothing was resolved.
 *
 * @param {Function} get - ms-agents GET verb.
 * @param {string} coachId - Coach id (user_id or employee_id).
 * @param {Object} [options] - `{ date_from, date_to, scope }`.
 * @returns {Promise<Object>} `{ coach_id, resolved_coach_user_id, date_from,
 *   date_to, totals }`.
 * @throws {Error} The original axios error (with `response.status` intact).
 */
export const getCoachSupportMetrics = async (
  get,
  coachId,
  { date_from, date_to, scope } = {}
) => {
  try {
    return await get(
      `/api/v1/oversight/analytics/coaches/${coachId}/support${buildQuery({
        date_from,
        date_to,
        scope,
      })}`
    );
  } catch (err) {
    console.error(err?.response?.data?.message || "Error fetching coach support metrics");
    throw err;
  }
};

/**
 * List oversight rooms, optionally filtered by one or more coach ids.
 *
 * @param {Function} get - ms-agents GET verb.
 * @param {Object} [options] - `{ limit, offset, coach_id }` where `coach_id`
 *   may be a single id or an array of ids (serialized as repeated params).
 * @returns {Promise<Object>} Rooms payload.
 * @throws {Error} The original axios error (with `response.status` intact).
 */
export const getOversightRooms = async (get, { limit, offset, coach_id } = {}) => {
  try {
    return await get(`/api/v1/oversight/rooms${buildQuery({ limit, offset, coach_id })}`);
  } catch (err) {
    console.error(err?.response?.data?.message || "Error fetching oversight rooms");
    throw err;
  }
};

/**
 * Classify an oversight analytics API error for UI handling.
 *
 * @param {Object} err - An axios-like error (`err.response.status` optional).
 * @returns {"forbidden"|"gateway"|"other"} `"forbidden"` for 403, `"gateway"`
 *   for 502/504 or a missing `response` (network failure), `"other"` otherwise.
 */
export const classifyOversightError = (err) => {
  const status = err?.response?.status;
  if (status === 403) return "forbidden";
  if (status === 502 || status === 504 || !err?.response) return "gateway";
  return "other";
};
